<template>
  <aside ref="sidebar" class="mc-livetrack--qa-sidebar">
    <div ref="sidebarWrapper" class="mc-livetrack--qa-sidebar-wrapper">
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <div class="mc-livetrack--qa-mobile-sidebar-estimate">
            <b-skeleton />
          </div>
        </template>
        <div
          v-if="statusRawType === 'ENDED'"
          ref="mobileSidebarActual"
          class="mc-livetrack--qa-mobile-sidebar-actual"
        >
          {{
            `${$t('fleetTaskInstant.text.completedOn')}: ${
              formatIndexDate(liveTrackResponse?.detail?.actual_arrival_on).date
            } ${formatIndexDate(liveTrackResponse?.detail?.actual_arrival_on).time}`
          }}
        </div>
        <div v-else ref="mobileSidebarEstimate" class="mc-livetrack--qa-mobile-sidebar-estimate">
          {{
            `${$t('fleetTaskInstant.text.estimatedCompletion')}: ${
              formatIndexDate(liveTrackResponse?.detail?.estimated_arrival_on).date
            } ${formatIndexDate(liveTrackResponse?.detail?.estimated_arrival_on).time}`
          }}
        </div>
      </b-skeleton-wrapper>
      <span
        ref="sidebarToggle"
        class="mc-livetrack--qa-sidebar-toggle"
        @touchstart="startDrag"
        @touchmove="moveDrag"
        @touchend="endDrag"
        @click="clickToggle"
      >
        <i class="mc-fa icon-fill_arrow-ios-left"></i>
        <i class="mc-fa icon-fill_arrow-ios-right"></i>
      </span>

      <portal-target
        v-show="isMobileView && selectedPointIdx !== null"
        name="mobile-detail-point"
        class="mc-livetrack--qa-detail-point"
      />

      <h4
        v-show="!isMobileView || (isMobileView && selectedPointIdx === null)"
        ref="sidebarHeader"
        class="mc-livetrack--qa-sidebar-header"
      >
        {{ $t('fleetTaskInstant.title.liveTrack', { foNumber: fleetTaskNumber }) }}
      </h4>

      <div
        v-show="!isMobileView || (isMobileView && selectedPointIdx === null)"
        ref="sidebarBody"
        class="mc-livetrack--qa-sidebar-body"
      >
        <div class="wizard">
          <McWizard
            ref="sidebarStatus"
            compact
            :items="wizardItems"
            :activeKey="statusRawType"
            class="mb-3"
          />
        </div>

        <McAlert
          v-if="statusRawType === 'CANCELED'"
          ref="sidebarReason"
          variant="error"
          class="alert"
        >
          <template #label>
            <div>
              <div class="__title">
                {{ $t('fleetTaskInstant.text.assignmentCancelled') }}
              </div>
              <div class="__reason">
                {{ `${$t('fleetTaskInstant.text.reason')}: ${canceledReason || '-'}` }}
              </div>
            </div>
          </template>
        </McAlert>

        <CurrentLocationCard
          v-show="isMobileView && statusRawType === 'STARTED'"
          ref="sidebarLocation"
          class="mc-livetrack--qa-current-location mb-3"
          :address="lastPosition"
          :loading="isLoading"
        />

        <div ref="sidebarBodyDetail" class="mc-livetrack--qa-sidebar-detail">
          <CollapseItem
            id="fleet-info"
            :title="$t('fleetTaskInstant.title.assignmentInformation')"
            accordion="fti-track-info"
          >
            <FleetInfo
              :driver="driverInfo"
              :vehicle="vehicleInfo"
              :note="noteInfo"
              :loading="isLoading"
            />
          </CollapseItem>

          <CollapseItem
            v-if="isDriverView && !isQaOnDemand"
            id="additional-info"
            accordion="fti-track-info"
            :title="$t('fleetTaskInstant.tab.additionalInformation')"
          >
            <AdditionalInfo />
          </CollapseItem>

          <CollapseItem
            id="timeline"
            :title="$t('fleetTaskInstant.title.route')"
            accordion="fti-track-info"
            :liveTrack="liveTrackResponse?.detail"
            :pointLength="timelineData?.length"
          >
            <TimelinePointsInfo
              :points="timelineData"
              :loading="isLoading"
              @show-detail-section="showDetailSection"
            />
          </CollapseItem>

          <CollapseItem
            id="task-history"
            :title="$t('fleetTaskInstant.title.assignmentHistory')"
            accordion="fti-track-info"
          >
            <TaskHistory :dataHistory="dataHistory" :loading="isLoading" />
          </CollapseItem>
        </div>
      </div>
    </div>
    <footer v-if="isMobileView" ref="sidebarFooter" class="mc-livetrack--qa-sidebar-footer">
      <span>
        {{
          $t('fleetTaskInstant.text.lastUpdatedOn', {
            date: formatIndexDate(currentDateTime).date,
            time: formatIndexDate(currentDateTime).time,
          })
        }}
      </span>
      <div>
        <span
          :hidden="
            getAsset('copyrightLogo', 'general') == null ||
            getAsset('copyrightLogo', 'general') == ''
          "
          >{{ $t('global.text.poweredBy') }} :
        </span>
        <figure class="d-inline-block mb-0">
          <img
            ref="imgCopyrightLogo"
            alt
            :src="getAsset('copyrightLogo', 'general')"
            @load="handleImageLoad()"
          />
        </figure>
      </div>
    </footer>
  </aside>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { formatIndexDate } from '@/helpers/tzDate'
import { GetAddressData } from '@/services/api/map'
import { getAddress, getGeolocation } from '@/services/api/geolocation'
import store from '@/state/store'
import McWizard from '@/components/McWizard'
import McAlert from '@/components/McAlert'

import CollapseItem from './CollapseItem'
import CurrentLocationCard from './CurrentLocationCard'
import FleetInfo from './FleetInfo'
import TimelinePointsInfo from './TimelinePointsInfo'
import TaskHistory from './TaskHistory'
import AdditionalInfo from './AdditionalInfo'

import { EventBus } from '../../../lib/event-bus'
import { wizardStatusItems } from '../../../lib/constants/wizard-items.js'

import debounce from 'lodash'

export default {
  components: {
    McWizard,
    McAlert,
    CurrentLocationCard,
    TimelinePointsInfo,
    FleetInfo,
    TaskHistory,
    AdditionalInfo,
    CollapseItem,
  },

  inject: ['isMobileView', 'isDriverView'],

  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    currentDateTime: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      dataDriver: {
        driver: null,
        vehicle: null,
      },
      dataHistory: [],
      lastPosition: '-',
      fleetTaskNumber: '',
      driverInfo: null,
      vehicleInfo: null,
      noteInfo: null,
      sidebarToggleOpen: true,
      isImageLoading: true,

      statusRawType: '',
      canceledReason: null,

      timelineData: [],

      sidebarHeight: 0,
      resizeListener: null,

      isDragged: false,
      startY: 0,
      deltaY: 0,
      sidebarMaxHeight: 0,
      sidebarMinHeight: 0,
    }
  },

  computed: {
    ...mapState('fleetTaskInstant', [
      'liveTrackResponse',
      'liveTrackVehicleResponse',
      'liveTrackDriverResponse',
      'selectedPointIdx',
    ]),
    ...mapGetters({
      getAsset: 'asset/getAsset',
    }),
    isLoading() {
      return this.loading || (this.isImageLoading && this.isMobileView)
    },
    wizardStatusItems() {
      return wizardStatusItems(this)
    },
    wizardItems() {
      if (this.statusRawType === 'CANCELED') {
        return [this.wizardStatusItems.STARTED, this.wizardStatusItems.CANCELED]
      }

      return [this.wizardStatusItems.STARTED, this.wizardStatusItems.ENDED]
    },
    isQaOnDemand() {
      return this.$store.state.identity.whoami?.accessRight === 'QA On Demand'
    },
  },

  watch: {
    isLoading: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.$nextTick(() => {
            this.calculateCompHeight(true)
            this.sidebarMaxHeight = window.innerHeight - 100
          })
        }
      },
    },
    sidebarHeight: {
      handler() {
        const isFullMap = document.body.classList.contains('mc-full-map')
        if (!isFullMap) {
          this.$nextTick(() => {
            this.calculateMapHeight(true)
          })
        }
      },
    },
  },

  mounted() {
    this.resizeListener = () => debounce(this.calculateCompHeight(true), 150)
    window.addEventListener('resize', this.resizeListener)
    EventBus.$on('calculate-comp-height', this.calculateCompHeight)
    EventBus.$on('success-fetch-live-track-data', this.handleSuccessFetch)
    this.resizeListener()

    if (this.isMobileView) return

    this.isImageLoading = false
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeListener)
    EventBus.$off('calculate-comp-height', this.calculateCompHeight)
    EventBus.$off('success-fetch-live-track-data', this.handleSuccessFetch)

    this.$store.dispatch('fleetTaskInstant/resetAllState')
  },

  methods: {
    formatIndexDate,
    async updateLastPosition() {
      const lat = this.liveTrackVehicleResponse?.vehicleStatus.latitude ?? null
      const lon = this.liveTrackVehicleResponse?.vehicleStatus.longitude ?? null
      if (lat === null || lon === null) return

      const useCache = store.state.identity.featureFlag.find(
        (feature) => feature.key == 'cache_geolocation'
      ).value
      if (useCache) {
        const address = await getGeolocation({ lat, lon })
        this.lastPosition = getAddress(address, lat, lon)?.name
      } else {
        const address = await GetAddressData(lat, lon)
        this.lastPosition = address.display_name
      }
    },
    async handleSuccessFetch() {
      if (this.isMobileView) {
        await this.updateLastPosition()
      } else {
        const lat = this.liveTrackVehicleResponse?.vehicleStatus.latitude ?? null
        const lng = this.liveTrackVehicleResponse?.vehicleStatus.longitude ?? null

        if (lat !== null && lng !== null) {
          EventBus.$emit('update-address', { lat, lng })
        }
      }

      await this.$nextTick()

      this.statusRawType = this.liveTrackResponse?.detail.status.raw_type ?? ''
      this.fleetTaskNumber = this.liveTrackResponse?.detail.number ?? ''
      this.timelineData = this.liveTrackResponse?.points ?? []
      this.dataHistory = this.liveTrackResponse?.status_history ?? []
      this.noteInfo = this.liveTrackResponse?.detail.note || null

      if (this.liveTrackResponse?.detail.driver.id) {
        this.driverInfo = {
          name: this.liveTrackResponse.detail.driver.name,
          phone_no: this.liveTrackResponse?.detail.driver.phone,
        }
      }

      if (this.liveTrackResponse?.detail.vehicle.id) {
        this.vehicleInfo = {
          license_plate: this.liveTrackResponse.detail.vehicle.license_plate,
        }
      }

      if (this.statusRawType === 'CANCELED') {
        this.canceledReason = this.liveTrackResponse.detail.status.status_fields.reason
      }
    },
    startDrag(e) {
      if (window.innerWidth >= 890) return
      this.startY = e.touches[0].clientY
    },
    moveDrag(e) {
      if (window.innerWidth >= 890) return
      e.preventDefault()
      this.isDragged = true
      const sidebar = this.$refs.sidebar
      const sidebarHeader = this.$refs.sidebarHeader
      const newHeight =
        window.innerHeight -
        e.touches[0].clientY +
        sidebarHeader.clientHeight +
        e.target.clientHeight / 2 +
        8

      if (newHeight >= this.sidebarMinHeight && newHeight <= this.sidebarMaxHeight) {
        sidebar.style.height = `${newHeight}px`
        sidebar.style.top = `calc(100% - ${newHeight}px)`
      } else {
        sidebar.style.height = `${
          newHeight >= this.sidebarMaxHeight ? this.sidebarMaxHeight : this.sidebarMinHeight
        }px`
        sidebar.style.top = `calc(100% - ${
          newHeight >= this.sidebarMaxHeight ? this.sidebarMaxHeight : this.sidebarMinHeight
        }px)`
      }

      this.deltaY = this.startY - e.touches[0].clientY
      this.startY = e.touches[0].clientY
    },
    endDrag() {
      if (!this.isDragged || window.innerWidth >= 890) return

      const isFullMap = document.body.classList.contains('mc-full-map')

      const isOpenDetail = document
        .querySelector('.mc-livetrack--qa')
        .classList.contains('detail-opened')

      if (((isFullMap && this.deltaY >= 0) || (!isFullMap && this.deltaY < 0)) && this.isDragged) {
        const sidebar = this.$refs.sidebar
        const height = isFullMap && this.deltaY >= 0 ? this.sidebarMaxHeight : this.sidebarMinHeight
        sidebar.style.height = `${height}px`
        sidebar.style.top = `calc(100% - ${height}px)`
      } else {
        this.toggleSidebarState()
        if (isOpenDetail) {
          this.handleSidebarDetailTransition(isFullMap)
        } else {
          this.handleSidebarTransition(isFullMap)
        }
      }

      this.isDragged = false
    },
    clickToggle() {
      if (this.isDragged) return

      const isFullMap = document.body.classList.contains('mc-full-map')

      this.toggleSidebarState()

      if (window.innerWidth >= 890) return

      const isOpenDetail = document
        .querySelector('.mc-livetrack--qa')
        .classList.contains('detail-opened')

      if (isOpenDetail) {
        this.handleSidebarDetailTransition(isFullMap)
      } else {
        this.handleSidebarTransition(isFullMap)
      }
    },
    toggleSidebarState() {
      document.body.classList.toggle('mc-full-map')
      this.sidebarToggleOpen = !this.sidebarToggleOpen
      this.$emit('toggleSidebar', this.sidebarToggleOpen)
    },
    handleSidebarDetailTransition(isFullMap) {
      const detailBody = document.querySelector('.detail-point-body')

      if (detailBody && isFullMap) {
        this.$nextTick(() => {
          detailBody.scrollTo({ top: 0 })
          this.calculateModalHeight(true)
        })
      } else {
        this.clearSidebarStyle()
      }
    },
    handleSidebarTransition(isFullMap) {
      const sidebarBody = this.$refs.sidebarBody
      if (sidebarBody && isFullMap) {
        this.$nextTick(() => {
          sidebarBody.scrollTo({ top: 0 })
          this.calculateModalHeight(true)
        })
      } else {
        this.clearSidebarStyle()
      }
    },
    calculateCompHeight(resize = false) {
      if (window.innerWidth >= 890) {
        this.clearSidebarStyle()
        const mapWrapper = document.getElementById('livetrack-map')
        const sidebarWrapper = this.$refs.sidebarWrapper
        if (mapWrapper && sidebarWrapper) {
          mapWrapper.style.height = ''
          sidebarWrapper.style.height = ''
        }
        return
      }

      if (resize) {
        const sidebarEl = this.$refs.sidebarBody
        if (sidebarEl) {
          sidebarEl.scrollTo({ top: 0 })
        }
      }

      this.calculateModalHeight()
      this.calculateMapHeight()
    },
    calculateMapHeight() {
      const mapWrapper = document.getElementById('livetrack-map')

      if (mapWrapper) {
        mapWrapper.style.height = `calc(100vh - ${this.sidebarHeight}px)`
      }
    },
    calculateModalHeight(withAnimation = false) {
      const isFullMap = document.body.classList.contains('mc-full-map')
      const isOpenDetail = document
        .querySelector('.mc-livetrack--qa')
        .classList.contains('detail-opened')

      const sidebar = this.$refs.sidebar
      const sidebarWrapper = this.$refs.sidebarWrapper
      const sidebarTime = this.$refs.mobileSidebarEstimate || this.$refs.mobileSidebarActual
      const sidebarToggle = this.$refs.sidebarToggle
      const sidebarFooter = this.$refs.sidebarFooter

      let sidebarHeight = 0
      if (isOpenDetail) {
        const sidebarPointHeader = document.querySelector('.detail-point-header')
        const sidebarPointThumbnail = document.querySelector('.livetrack-thumbnail')

        if (sidebarTime && sidebarToggle && sidebarPointHeader && sidebarPointThumbnail) {
          sidebarHeight +=
            sidebarTime.offsetHeight +
            sidebarToggle.offsetHeight +
            sidebarPointHeader.clientHeight +
            sidebarPointThumbnail.clientHeight +
            sidebarFooter.offsetHeight +
            30
        }
      } else {
        const sidebarHeader = this.$refs.sidebarHeader
        const sidebarStatus = this.$refs.sidebarStatus.$el
        const sidebarReason = this.$refs.sidebarReason

        if (sidebarReason) {
          sidebarHeight = sidebarReason.offsetHeight
        }

        if (
          sidebarTime &&
          sidebarToggle &&
          sidebarHeader &&
          sidebarStatus &&
          sidebarFooter &&
          !isFullMap
        ) {
          sidebarHeight +=
            sidebarTime.offsetHeight +
            sidebarToggle.offsetHeight +
            sidebarHeader.offsetHeight +
            sidebarStatus.offsetHeight +
            sidebarFooter.offsetHeight +
            128
        }
      }

      if (sidebar && sidebarWrapper) {
        if (!withAnimation) {
          sidebar.style.transition = 'none'
        }

        sidebarWrapper.style.height = `calc(100% - ${sidebarFooter.offsetHeight}px)`

        if (sidebarHeight) {
          this.sidebarHeight = sidebarHeight
          sidebar.style.height = `${sidebarHeight}px`
          sidebar.style.top = `calc(100% - ${sidebarHeight}px)`
          this.sidebarMinHeight = this.sidebarHeight
        }

        if (!withAnimation) {
          sidebar.style.transition = ''
        }
      }
    },
    clearSidebarStyle() {
      const sidebar = this.$refs.sidebar
      if (sidebar) {
        sidebar.style.height = ''
        sidebar.style.top = ''
      }
    },
    handleImageLoad() {
      this.isImageLoading = false
    },
    async showDetailSection(index) {
      if (this.isMobileView) {
        const marginBottom = document.querySelector('.mc-livetrack--qa-sidebar-footer').clientHeight
        document.querySelector('.detail-point-body').style.marginBottom = `${marginBottom + 20}px`
      }

      this.calculateCompHeight()

      this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', index)

      EventBus.$emit('focus-point', true)
      EventBus.$emit('get-point-detail')
    },
  },
}
</script>

<style lang="scss" scoped>
.wizard {
  display: flex;
  justify-content: center;
}

:deep(.alert) {
  .__title {
    color: $mep-color-text;
    font-weight: 500;
  }

  .__reason {
    font-size: 12px;
  }
}
</style>
